import React from 'react';
import { Box, Grid, TextField, IconButton, Typography } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, ViewList as ViewListIcon, ViewModule as ViewModuleIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';

function ToyListControls({ 
  searchTerm, 
  onSearchChange, 
  onClearSearch, 
  viewMode, 
  onViewModeChange, 
  filteredToys, 
  userCurrency 
}) {
  const { t } = useTranslation();

  const calculateTotalValue = () => {
    return filteredToys.reduce((sum, toy) => {
      const price = toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate 
        ? toy.preorder_price 
        : (toy.price || 0);
      return sum + price;
    }, 0);
  };

  const calculateAveragePrice = () => {
    if (filteredToys.length === 0) return 0;
    const totalValue = calculateTotalValue();
    return Math.round(totalValue / filteredToys.length);
  };

  const calculateTotalQuantity = () => {
    return filteredToys.reduce((sum, toy) => sum + (toy.qty || 1), 0);
  };

  return (
    <Box sx={{
      background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
      p: 3,
      borderRadius: 3,
      mb: 4,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    }}>
      {/* Search and View Controls */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        {/* Search Field */}
        <Box sx={{ flexGrow: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('collection.searchPlaceholder')}
            value={searchTerm}
            onChange={onSearchChange}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: searchTerm && (
                <IconButton onClick={onClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              )
            }}
          />
        </Box>

        {/* View Mode Toggle */}
        <Box sx={{ 
          display: 'flex', 
          gap: 0.5,
          bgcolor: 'grey.100',
          borderRadius: 1,
          p: 0.5
        }}>
          <IconButton
            onClick={() => onViewModeChange('grid')}
            color={viewMode === 'grid' ? 'primary' : 'default'}
            size="small"
            sx={{
              bgcolor: viewMode === 'grid' ? 'white' : 'transparent',
              boxShadow: viewMode === 'grid' ? 1 : 'none',
              '&:hover': {
                bgcolor: viewMode === 'grid' ? 'white' : 'rgba(0,0,0,0.04)'
              }
            }}
            title="Grid View"
          >
            <ViewModuleIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => onViewModeChange('list')}
            color={viewMode === 'list' ? 'primary' : 'default'}
            size="small"
            sx={{
              bgcolor: viewMode === 'list' ? 'white' : 'transparent',
              boxShadow: viewMode === 'list' ? 1 : 'none',
              '&:hover': {
                bgcolor: viewMode === 'list' ? 'white' : 'rgba(0,0,0,0.04)'
              }
            }}
            title="List View"
          >
            <ViewListIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Box sx={{ pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {filteredToys.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? t('collection.summary.foundToys') : t('collection.summary.totalToys')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {userCurrency} {calculateTotalValue().toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('collection.summary.totalValue')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {calculateAveragePrice().toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('collection.summary.averagePrice')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {calculateTotalQuantity()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('collection.summary.totalQuantity')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default ToyListControls; 